<template>
  <a-layout-header
    class="flex items-center justify-between"
    :style="{ height: '48px', padding: '0 16px', background: 'white' }"
  >
    <div class="pl-2.5 flex items-center w-full">
      <div
        class="flex items-center cursor-pointer"
        @click="
          () => {
            router.push('/login');
          }
        "
      >
        <img
          v-if="showCustomContent"
          src="@/assets/images/gomi-kyoto-logo.jpg"
          alt="Gomi Kyoto Logo"
          class="h-24 mt-14"
        />
        <Logo v-else />
      </div>
    </div>
  </a-layout-header>
  <br /><br />
  <div class="account-creation">
    <div v-if="showCustomContent" class="custom-text-container">
      <p>
        このページは「ごみ.Kyoto+」での無料見積結果をもとに、正式な依頼・管理を行うための【専用アカウント作成ページ】です。
      </p>
      <p>ご登録いただいても、契約や請求は直ちに発生しません。</p>
      <p>契約内容や費用をご確認いただいた上で、正式なご依頼に進んでいただけます。</p>
    </div>
    <h2 class="account-creation__header text-gray-500">{{ $t('account_creation') }}</h2>
    <p class="text-gray-500 text-center mb-[50px]">
      {{ $t('パスワードは半角英数(8~24文字)を使用できます。') }}
    </p>
    <a-form @finish="handleSubmit" class="account-creation__form" :model="form" :rules="formRules">
      <input-slot
        :label="$t('email')"
        inputId="email_address"
        :label-span="{ xs: 24, sm: 8, md: 6 }"
        :inputSpan="{ xs: 24, sm: 16, md: 18 }"
        :required="true"
        :readonly="false"
      >
        <a-form-item name="email" :rules="formRules.email">
          <a-input ref="emailInput" v-model:value="form.email" maxlength="254" />
        </a-form-item>
      </input-slot>

      <input-slot
        :label="$t('password')"
        inputId="password"
        :label-span="{ xs: 24, sm: 8, md: 6 }"
        :inputSpan="{ xs: 24, sm: 16, md: 18 }"
        :required="true"
        :readonly="false"
      >
        <a-form-item name="password" :rules="formRules.password">
          <a-input-password
            maxlength="24"
            :placeholder="$t('password')"
            v-model:value="form.password"
          />
        </a-form-item>
      </input-slot>

      <input-slot
        :label-span="{ xs: 24, sm: 8, md: 6 }"
        :inputSpan="{ xs: 24, sm: 16, md: 18 }"
        :label="$t('confirm_password')"
        inputId="confirm_password"
        :required="true"
        :readonly="false"
      >
        <a-form-item name="confirmPassword" :rules="formRules.confirmPassword">
          <a-input-password
            :placeholder="$t('confirmation_password')"
            maxlength="24"
            v-model:value="form.confirmPassword"
          />
        </a-form-item>
      </input-slot>
      <div class="text-center" v-if="isEmailExisted">
        <span class="text-red-500 text-base">{{ $t('MSG_04_1') }}</span>
      </div>
      <br />
      <a-form-item>
        <div class="text-center">
          <a-button html-type="submit" class="md:w-2/5 py-2" type="primary">
            {{ $t('create') }}
          </a-button>
        </div>
      </a-form-item>
    </a-form>

    <div class="custom-text-container bottom-text">
      <p>※産業廃棄物（粗大ごみ）の処理をご希望の方へ</p>
      <p>アカウント作成前に、下記のフォームより【EDIシステム利用申し込み】をお願いいたします。</p>
      <p>※産業廃棄物処理依頼のために必ず必要です。</p>
      <p>
        <a
          href="https://docs.google.com/forms/d/e/1FAIpQLSdRUx8FnChOIBLCOrNHQhu1P8wVODc4-3DsOez-B922QeIW6w/viewform"
          target="_blank"
          rel="noopener noreferrer"
          >▶︎ 産業廃棄物（EDI）システム利用申請フォームはこちら</a
        >
      </p>
      <p>
        ※当システムは、産業廃棄物の処理においてJWNET（公益社団法人日本産業廃棄物処理振興センター）提供の
      </p>
      <p>「電子マニフェスト」システムとの連携を前提に設計されています。</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Logo from '@/components/Logo.vue';
import { useI18n } from 'vue-i18n';
import type { userCredentials } from './const';
import InputSlot from '../components/InputSlot.vue';
import { useAuthStore } from '@/stores';
import { message, Modal } from 'ant-design-vue';
import { router } from '@/router';
import { setApiDisabled } from '@/services/http';
import { formatModalTitle } from '@/utils/modules/message';
import { useRoute } from 'vue-router';

const { signUp } = useAuthStore();

const { t } = useI18n();
const isSentEmail = ref(false);
const isLoading = ref(false);
const isEmailExisted = ref(false);
const emailInput = ref<HTMLElement | null>(null);
const route = useRoute();

const showCustomContent = computed(() => {
  const { type, pcode, size, num, week } = route.query;
  return (
    type &&
    pcode &&
    size &&
    num &&
    week &&
    type !== '' &&
    pcode !== '' &&
    size !== '' &&
    Number(num) !== 0 &&
    Number(week) !== 0
  );
});

const form = ref({
  email: '',
  userName: '',
  password: '',
  confirmPassword: '',
  role: 'WASTE'
});

const formRules = {
  email: [
    { required: true, message: t('MSG_00_21'), trigger: ['blur', 'change'] },
    { type: 'email', message: t('MSG_00_22'), trigger: ['blur', 'change'] }
  ],
  password: [
    {
      validator: (rule: any, value: any, callback: any) => {
        if (!value) {
          callback(new Error(t('MSG_00_21')));
        } else if (value.length < 8) {
          callback(new Error(t('MSG_03_1')));
        } else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(value)) {
          callback(new Error(t('MSG_03_1')));
        } else {
          callback();
        }
      },
      trigger: 'blur'
    }
  ],
  confirmPassword: [
    {
      validator: (rule: any, value: string, callback: any) => {
        if (!value) {
          callback(new Error(t('MSG_00_21')));
        } else if (value !== form.value.password) {
          callback(t('MSG_03_2'));
        } else {
          callback();
        }
      },
      trigger: 'blur'
    }
  ]
};

onMounted(() => {
  emailInput.value?.focus();
  setApiDisabled(false);
});

const handleSubmit = async () => {
  isLoading.value = true;
  const { confirmPassword, ...formData } = form.value;

  const userData: userCredentials = {
    email: formData.email,
    userName: formData.userName,
    password: formData.password,
    role: formData.role,
    type: (route.query.type as string) || '',
    pcode: (route.query.pcode as string) || '',
    size: (route.query.size as string) || '',
    num: Number(route.query.num) || 0,
    week: Number(route.query.week) || 0
  };

  let modal = Modal.confirm({
    title: '',
    okText: t('cancel_button'),
    okType: 'danger',
    cancelText: t('yes'),
    cancelButtonProps: {
      type: 'primary',
      onClick: async () => {
        await signUp(userData)
          .then(() => {
            message.success(t('MSG_04_3', { email: form.value.email }));
            isLoading.value = false;
            isEmailExisted.value = false;
            modal.destroy();
            router.push('/login');
          })
          .catch(() => {
            isEmailExisted.value = true;
            modal.destroy();
          });
      }
    },
    okButtonProps: { onClick: () => modal.destroy() },
    centered: true,
    maskClosable: true
  });
  const title = t('MSG_04_2');
  modal.update({
    title: formatModalTitle(title)
  });
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.custom-text-container {
  text-align: center;
  margin-bottom: 40px;
  padding: 20px;
  p {
    color: red;
    margin-bottom: 15px;
  }
}

.bottom-text p {
  color: red;
}

.bottom-text a {
  color: #5b9ad5 !important;
}

.account-creation {
  width: 100%;
  padding: 0 20px 20px;
  margin-top: 10px;

  @media (min-width: 1301px) {
    margin-top: -30px;
  }

  &__header {
    text-align: center;
    font-size: 24px;
    margin-bottom: 25px;
    font-weight: bold;
  }

  &__form {
    max-width: 600px;
    width: 100%;
    margin: 20px auto;
    padding: 20px;
    .ant-form-item {
      margin-bottom: 24px;
    }
    .ant-form-item-label {
      font-weight: bold;
    }

    .ant-form-item-explain {
      font-size: 12px;
      color: $red_color;
    }

    .ant-btn-primary {
      background-color: $blue_color;
      border-color: $blue_color;
    }
  }
}
</style>
